<template>
    <div
        class="language-selector-image"
        :class="{ mobile: isMobile }"
        ref="langSelector"
    >
        <!-- 当前语言按钮 (使用 select-lang-btn 图片) -->
        <div class="lang-current-btn" @click.stop="toggleDropdown">
            <img
                :src="getCurrentButtonImage()"
                class="lang-btn-img"
                :alt="currentLang.name"
            />
        </div>

        <!-- 下拉菜单 - 显示所有语言 -->
        <transition name="dropdown-fade">
            <div v-show="showDropdown" class="lang-dropdown" @click.stop>
                <div
                    v-for="lang in languages"
                    :key="lang.code"
                    class="lang-option"
                    :class="{ active: lang.code === currentLanguage }"
                    @click.stop="selectLanguage(lang.code)"
                    @mouseenter="hoveredLang = lang.code"
                    @mouseleave="hoveredLang = null"
                >
                    <img
                        :src="getLangOptionImage(lang.code)"
                        class="lang-option-img"
                        :alt="lang.name"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
/**
 * LanguageSelectorImage 图片式语言选择器组件
 *
 * 功能：
 * - 使用图片资源展示语言选项
 * - 当前选中语言显示为按钮样式 (select-lang-btn-{code}.png)
 * - 下拉菜单中显示普通/悬停状态 (lang-{code}.png / lang-{code}-hover.png)
 * - 支持PC和移动端响应式样式
 *
 * Props:
 * - languages: 可选语言列表 [{code, name}]
 * - currentLanguage: 当前选中的语言代码
 * - isMobile: 是否为移动端模式
 *
 * Events:
 * - language-change: 语言切换事件，参数为新语言代码
 */
export default {
    name: "LanguageSelectorImage",

    props: {
        // 可选语言列表
        languages: {
            type: Array,
            required: true,
            validator: langs => {
                return langs.every(lang => lang.code && lang.name);
            }
        },
        // 当前选中的语言代码
        currentLanguage: {
            type: String,
            required: true
        },
        // 是否为移动端
        isMobile: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            showDropdown: false,
            hoveredLang: null // 当前悬停的语言代码
        };
    },

    computed: {
        // 当前语言对象
        currentLang() {
            return (
                this.languages.find(
                    lang => lang.code === this.currentLanguage
                ) || this.languages[0]
            );
        }
    },

    methods: {
        // 获取当前语言按钮图片
        getCurrentButtonImage() {
            try {
                return require(`@/assets/language-selector/select-lang-btn-${this.currentLanguage}.png`);
            } catch (error) {
                console.error(
                    `找不到按钮图片: select-lang-btn-${this.currentLanguage}.png`
                );
                return "";
            }
        },

        // 获取语言选项图片 (根据悬停状态)
        getLangOptionImage(langCode) {
            try {
                // 如果是当前悬停的语言，使用 hover 图片
                if (this.hoveredLang === langCode) {
                    return require(`@/assets/language-selector/lang-${langCode}-hover.png`);
                }
                // 否则使用普通图片
                return require(`@/assets/language-selector/lang-${langCode}.png`);
            } catch (error) {
                // 如果找不到普通图片，尝试使用 hover 图片作为备用
                try {
                    console.warn(
                        `找不到 lang-${langCode}.png，使用 hover 版本作为备用`
                    );
                    return require(`@/assets/language-selector/lang-${langCode}-hover.png`);
                } catch (fallbackError) {
                    console.error(
                        `找不到语言图片: lang-${langCode}.png 和 lang-${langCode}-hover.png`
                    );
                    return "";
                }
            }
        },

        // 切换下拉菜单显示状态
        toggleDropdown() {
            this.showDropdown = !this.showDropdown;
        },

        // 选择语言
        selectLanguage(langCode) {
            this.showDropdown = false;
            this.hoveredLang = null;
            this.$emit("language-change", langCode);
        },

        // 处理点击外部关闭下拉菜单
        handleClickOutside(event) {
            if (
                this.$refs.langSelector &&
                !this.$refs.langSelector.contains(event.target)
            ) {
                this.showDropdown = false;
                this.hoveredLang = null;
            }
        }
    },

    mounted() {
        // 监听点击外部事件
        document.addEventListener("click", this.handleClickOutside);
    },

    beforeDestroy() {
        // 清理事件监听
        document.removeEventListener("click", this.handleClickOutside);
    }
};
</script>

<style scoped lang="less">
.language-selector-image {
    position: relative;
    cursor: pointer;
    user-select: none;
    z-index: 1000;
}

/* 当前语言按钮 */
.lang-current-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
    }

    &:active {
        transform: scale(0.98);
    }
}

.lang-btn-img {
    display: block;
    width: 150px; /* 固定宽度 */
    height: auto; /* 高度自适应 */
    object-fit: contain;
    transition: all 0.3s ease;
    border-radius: 0; /* 确保无圆角 */
}

/* 下拉菜单容器 - 无背景，仅作为布局容器，紧贴按钮 */
.lang-dropdown {
    position: absolute;
    top: 100%; /* 紧贴按钮，无间隙 */
    left: 50%;
    transform: translateX(-50%);
    background: transparent;
    border-radius: 0;
    overflow: visible;
    box-shadow: none;
    padding: 0;
    min-width: auto;
    backdrop-filter: none;
    border: none;
    margin-top: -12px;
}

/* 语言选项 - 紧密排列 */
.lang-option {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 0;

    &:not(:last-child) {
        margin-bottom: 0; /* 紧密排列，无间距 */
    }

    &:hover {
        transform: scale(1.05);
        position: relative;
        z-index: 10;
    }
}

.lang-option-img {
    display: block;
    width: 150px; /* 固定宽度，与按钮一致 */
    height: auto; /* 高度自适应 */
    object-fit: contain;
    transition: all 0.2s ease;
    border-radius: 0;
}

/* 下拉动画 */
.dropdown-fade-enter-active {
    animation: dropdown-appear 0.3s ease-out;
}

.dropdown-fade-leave-active {
    animation: dropdown-disappear 0.2s ease-in;
}

@keyframes dropdown-appear {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

@keyframes dropdown-disappear {
    from {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
    to {
        opacity: 0;
        transform: translateX(-50%) translateY(-5px);
    }
}

/* 移动端适配 */
.language-selector-image.mobile {
    .lang-btn-img {
        width: 180px;
    }

    .lang-option-img {
        width: 180px; /* 统一尺寸 */
    }
}

/* 响应式设计 */
@media (min-width: 1920px) {
    .lang-btn-img {
        width: 170px;
    }

    .lang-option-img {
        width: 170px; /* 统一尺寸 */
    }
}

@media (min-width: 1440px) and (max-width: 1919px) {
    .lang-btn-img {
        width: 160px;
    }

    .lang-option-img {
        width: 160px; /* 统一尺寸 */
    }
}

@media (min-width: 940px) and (max-width: 1439px) {
    .lang-btn-img {
        width: 140px;
    }

    .lang-option-img {
        width: 140px; /* 统一尺寸 */
    }
}

@media (max-width: 939px) {
    .lang-btn-img {
        width: 200px; /* WAP 模式 */
    }

    .lang-option-img {
        width: 200px; /* 统一尺寸 */
    }
}
</style>
